import { useEffect, useRef, useState, type AnimationEvent } from "react";
import { useT } from "../../lib/i18n";
import { COLLAB_COLORS, colorOf, type CollabPeer } from "../../lib/collab";
import { newPartyState, stepParty, type PartyEvent, type PartyPoint } from "../../lib/cursor-party";
import "./cursor-party.css";

/**
 * 协同鼠标彩蛋：同一页上的鼠标碰到一起时放点小动静（判定在 lib/cursor-party.ts）。
 * - 碰上：两人颜色的光圈 + 小星星，弹一个「击掌！」，连着碰会数连击
 * - 撞得快：「咚！」，火花更多，两人的鼠标头上转几圈小星星
 * - 贴着不走：隔一会儿冒一颗两人颜色渐变的心
 * - 碰到空闲的人：他头上飘 Zzz
 * - 三个以上凑一块：彩纸
 * 只在「显示别人的鼠标」开着时挂上（CollabLayer 管）；不挡点击；系统设了减弱动态就只淡入淡出几个字，不放粒子。
 */

type Shape = "dot" | "star" | "spark" | "confetti";
interface Particle { x: number; y: number; vx: number; vy: number; g: number; drag: number; age: number; ttl: number; size: number; color: string; shape: Shape; rot: number; vr: number }
interface Ring { x: number; y: number; r0: number; r1: number; age: number; ttl: number; c1: string; c2: string; w: number }
/** 被撞晕：鼠标头上转的小星星，跟着那个鼠标走 */
interface Dizzy { id: string; age: number; ttl: number; colors: string[] }
type Floater =
  | { key: number; kind: "label"; slot: string; x: number; y: number; text: string; combo: number; c1: string; c2: string; bonk?: boolean; big?: boolean }
  | { key: number; kind: "heart"; x: number; y: number; c1: string; c2: string }
  | { key: number; kind: "zzz"; x: number; y: number };
type NewFloater = Floater extends infer F ? (F extends Floater ? Omit<F, "key"> : never) : never;

const ME = "\0me";
const MAX_PARTICLES = 500;
const MAX_FLOATERS = 12;

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const easeOut = (t: number) => 1 - (1 - t) ** 3;

/** 四角小星（闪光那种） */
function starPath(ctx: CanvasRenderingContext2D, r: number) {
  const k = r * 0.32;
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.quadraticCurveTo(k, -k, r, 0);
  ctx.quadraticCurveTo(k, k, 0, r);
  ctx.quadraticCurveTo(-k, k, -r, 0);
  ctx.quadraticCurveTo(-k, -k, 0, -r);
  ctx.closePath();
}

export function CursorParty({ peers, me }: { peers: CollabPeer[]; me: string }) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [floaters, setFloaters] = useState<Floater[]>([]);
  const peersRef = useRef(peers);
  peersRef.current = peers;
  const meRef = useRef(me);
  meRef.current = me;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const particles: Particle[] = [], rings: Ring[] = [], dizzies: Dizzy[] = [];
    const state = newPartyState();
    /** 这一帧每个鼠标在屏幕上的位置（星星跟着转要用） */
    let where = new Map<string, { x: number; y: number }>();
    let mouse: { x: number; y: number } | null = null;
    let key = 0, raf = 0, last = performance.now();

    const fit = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(window.innerWidth * dpr);
      canvas.height = Math.round(window.innerHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();
    const onMove = (e: MouseEvent) => { mouse = { x: e.clientX, y: e.clientY }; };
    const onOut = (e: MouseEvent) => { if (!e.relatedTarget) mouse = null; };
    window.addEventListener("resize", fit);
    window.addEventListener("mousemove", onMove, { passive: true, capture: true });
    document.addEventListener("mouseout", onOut);

    const nameOf = (id: string) => (id === ME ? meRef.current : peersRef.current.find((p) => p.id === id)?.name ?? "");
    /** 同一对人（或同一次聚会）的字只留最新一个：连着撞时一层层叠上去就糊了 */
    const float = (f: NewFloater) =>
      setFloaters((fs) => [...fs.filter((x) => !(f.kind === "label" && x.kind === "label" && x.slot === f.slot)).slice(-(MAX_FLOATERS - 1)), { ...f, key: ++key } as Floater]);
    /** 被碰的那个鼠标弹一下（自己的是系统鼠标，弹不了） */
    const recoil = (id: string, strong: boolean) => {
      if (id === ME || reduced.matches) return;
      const svg = document.querySelector(`[data-cursor-id="${CSS.escape(id)}"] svg`);
      svg?.animate?.([
        { transform: "scale(1) rotate(0)" },
        { transform: `scale(${strong ? 1.5 : 1.3}) rotate(${strong ? -22 : -12}deg)` },
        { transform: "scale(.92) rotate(6deg)" },
        { transform: "scale(1) rotate(0)" },
      ], { duration: strong ? 520 : 380, easing: "cubic-bezier(.3,1.4,.5,1)" });
    };
    const burst = (x: number, y: number, colors: string[], n: number, speed: [number, number], shapes: Shape[]) => {
      for (let i = 0; i < n && particles.length < MAX_PARTICLES; i++) {
        const a = (i / n) * Math.PI * 2 + rand(-0.3, 0.3), v = rand(speed[0], speed[1]);
        const shape = shapes[i % shapes.length];
        particles.push({
          x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - (shape === "confetti" ? 0 : 30),
          g: shape === "spark" ? 120 : shape === "confetti" ? 520 : 260, drag: shape === "confetti" ? 2.2 : 3.2,
          age: 0, ttl: rand(0.55, 0.95) * (shape === "confetti" ? 2.2 : 1), size: shape === "star" ? rand(3.5, 5.5) : shape === "confetti" ? rand(5, 8) : rand(1.8, 3.2),
          color: colors[i % colors.length], shape, rot: rand(0, Math.PI * 2), vr: rand(-10, 10),
        });
      }
    };
    const fountain = (x: number, y: number, colors: string[], n: number) => {
      for (let i = 0; i < n && particles.length < MAX_PARTICLES; i++) {
        const a = -Math.PI / 2 + rand(-0.9, 0.9), v = rand(260, 520);
        particles.push({
          x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, g: 620, drag: 1.6, age: 0, ttl: rand(1.6, 2.4),
          size: rand(5, 9), color: colors[i % colors.length], shape: "confetti", rot: rand(0, Math.PI * 2), vr: rand(-12, 12),
        });
      }
    };

    const play = (e: PartyEvent) => {
      const tr = tRef.current;
      if (e.kind === "bump") {
        const c1 = colorOf(nameOf(e.a)), c2 = colorOf(nameOf(e.b));
        // 连击到 5 下起彩虹
        const colors = e.combo >= 5 ? COLLAB_COLORS : [c1, c2, "#fff4b8"];
        float({ kind: "label", slot: [e.a, e.b].sort().join("\n"), x: e.x, y: e.y - 14, text: tr(e.hard ? "collab.party.bonk" : "collab.party.highFive"), combo: e.combo, c1, c2, bonk: e.hard, big: e.combo >= 5 });
        recoil(e.a, e.hard);
        recoil(e.b, e.hard);
        if (reduced.matches) return;
        rings.push({ x: e.x, y: e.y, r0: 4, r1: e.hard ? 46 : 32, age: 0, ttl: 0.5, c1, c2, w: e.hard ? 4 : 3 });
        if (e.combo >= 3) rings.push({ x: e.x, y: e.y, r0: 2, r1: 54 + e.combo * 3, age: -0.08, ttl: 0.6, c1: c2, c2: c1, w: 2 });
        const n = Math.min(18 + (e.combo - 1) * 4, 40) + (e.hard ? 10 : 0);
        burst(e.x, e.y, colors, n, e.hard ? [180, 380] : [110, 240], e.hard ? ["spark", "star", "spark", "dot"] : ["dot", "star", "dot"]);
        if (e.hard) for (const id of [e.a, e.b]) {
          const i = dizzies.findIndex((d) => d.id === id);
          if (i >= 0) dizzies.splice(i, 1);
          dizzies.push({ id, age: 0, ttl: 1.6, colors: ["#ffd43b", c1, c2] });
        }
      } else if (e.kind === "heart") {
        const c1 = colorOf(nameOf(e.a)), c2 = colorOf(nameOf(e.b));
        float({ kind: "heart", x: e.x, y: e.y - 10, c1, c2 });
        if (!reduced.matches) burst(e.x, e.y - 16, [c1, c2, "#ffc9de"], 6, [40, 90], ["star", "dot"]);
      } else if (e.kind === "poke") {
        float({ kind: "zzz", x: e.x + 16, y: e.y - 4 });
        recoil(e.who, false);
        if (!reduced.matches) rings.push({ x: e.x, y: e.y, r0: 3, r1: 22, age: 0, ttl: 0.45, c1: "#c0c4cc", c2: "#909399", w: 2 });
      } else {
        const colors = e.ids.map((id) => colorOf(nameOf(id)));
        float({ kind: "label", slot: "party", x: e.x, y: e.y - 18, text: `🎉 ${tr("collab.party.group", { n: e.ids.length })}`, combo: 1, c1: colors[0], c2: colors[colors.length - 1], big: true });
        for (const id of e.ids) recoil(id, true);
        if (reduced.matches) return;
        rings.push({ x: e.x, y: e.y, r0: 6, r1: 70, age: 0, ttl: 0.7, c1: colors[0], c2: colors[1] ?? colors[0], w: 4 });
        fountain(e.x, e.y, [...colors, "#ffd43b", "#fff"], 70);
        burst(e.x, e.y, colors, 18, [140, 260], ["star", "dot"]);
      }
    };

    /** 这一刻屏幕上的鼠标：别人的读画出来的位置（含 200ms 补间，跟眼睛看到的一致），自己的读真鼠标、只算内容区里的 */
    const sample = (): PartyPoint[] => {
      const pts: PartyPoint[] = [];
      const main = document.querySelector("[data-collab-main]")?.getBoundingClientRect();
      if (mouse && main && document.visibilityState === "visible"
        && mouse.x >= main.left && mouse.x <= main.right && mouse.y >= main.top && mouse.y <= main.bottom) pts.push({ id: ME, x: mouse.x, y: mouse.y });
      for (const p of peersRef.current) {
        if (!p.cursor) continue;
        const r = document.querySelector(`[data-cursor-id="${CSS.escape(p.id)}"]`)?.getBoundingClientRect();
        if (r) pts.push({ id: p.id, x: r.left + 1, y: r.top + 1, ...(p.idleMin != null ? { idle: true } : {}) });
      }
      return pts;
    };

    const draw = (dt: number) => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (let i = rings.length - 1; i >= 0; i--) {
        const r = rings[i];
        r.age += dt;
        if (r.age >= r.ttl) { rings.splice(i, 1); continue; }
        if (r.age < 0) continue;
        const k = r.age / r.ttl, rad = r.r0 + (r.r1 - r.r0) * easeOut(k);
        const grad = ctx.createLinearGradient(r.x - rad, r.y - rad, r.x + rad, r.y + rad);
        grad.addColorStop(0, r.c1);
        grad.addColorStop(1, r.c2);
        ctx.globalAlpha = (1 - k) ** 1.5;
        ctx.strokeStyle = grad;
        ctx.lineWidth = Math.max(0.5, r.w * (1 - k));
        ctx.beginPath();
        ctx.arc(r.x, r.y, rad, 0, Math.PI * 2);
        ctx.stroke();
      }
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.age += dt;
        if (p.age >= p.ttl) { particles.splice(i, 1); continue; }
        const damp = Math.exp(-p.drag * dt);
        p.vx *= damp;
        p.vy = p.vy * damp + p.g * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        const k = p.age / p.ttl;
        ctx.globalAlpha = 1 - k * k;
        ctx.fillStyle = p.color;
        ctx.strokeStyle = p.color;
        if (p.shape === "dot") {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * (1 - k * 0.5), 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === "spark") {
          const sp = Math.hypot(p.vx, p.vy) || 1, len = Math.min(14, 4 + sp * 0.03);
          ctx.lineWidth = 2 * (1 - k * 0.6);
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x - (p.vx / sp) * len, p.y - (p.vy / sp) * len);
          ctx.stroke();
        } else {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          if (p.shape === "star") { starPath(ctx, p.size * (1 - k * 0.4)); ctx.fill(); }
          // 彩纸：纵向随转角缩放，看起来在翻
          else { ctx.scale(1, Math.cos(p.rot * 1.7)); ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2); }
          ctx.restore();
        }
      }
      for (let i = dizzies.length - 1; i >= 0; i--) {
        const d = dizzies[i];
        d.age += dt;
        const at = where.get(d.id);
        if (d.age >= d.ttl || !at) { dizzies.splice(i, 1); continue; }
        const fade = Math.min(1, (d.ttl - d.age) / 0.35, d.age / 0.12);
        for (let s = 0; s < 3; s++) {
          const a = d.age * 9 + (s * Math.PI * 2) / 3;
          const sx = at.x + 6 + Math.cos(a) * 13, sy = at.y - 7 + Math.sin(a) * 4.5;
          // 转到后面的那半圈小一点、淡一点
          const front = (Math.sin(a) + 1) / 2;
          ctx.globalAlpha = fade * (0.45 + 0.55 * front);
          ctx.fillStyle = d.colors[s % d.colors.length];
          ctx.save();
          ctx.translate(sx, sy);
          ctx.rotate(a);
          starPath(ctx, 2.6 + front * 1.6);
          ctx.fill();
          ctx.restore();
        }
      }
      ctx.globalAlpha = 1;
    };

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const pts = sample();
      where = new Map(pts.map((p) => [p.id, p]));
      for (const e of stepParty(state, pts, now)) play(e);
      // 最后一个粒子走完的那一帧 draw 已经清过画布，之后空着就不用再画
      if (particles.length || rings.length || dizzies.length) draw(dt);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      window.removeEventListener("mousemove", onMove, { capture: true });
      document.removeEventListener("mouseout", onOut);
    };
  }, []);

  const done = (k: number) => (e: AnimationEvent) => { if (e.target === e.currentTarget) setFloaters((fs) => fs.filter((f) => f.key !== k)); };
  return (
    <>
      <canvas ref={canvasRef} aria-hidden className="absolute inset-0 h-full w-full" />
      {floaters.map((f) => (
        f.kind === "label" ? (
          <div key={f.key} onAnimationEnd={done(f.key)} className={`cp-float cp-label${f.bonk ? " cp-bonk" : ""}${f.big ? " cp-big" : ""}`}
            style={{ left: f.x, top: f.y, background: `linear-gradient(90deg, ${f.c1}, ${f.c2})` }}>
            {f.text}{f.combo >= 2 && <b>×{f.combo}</b>}
          </div>
        ) : f.kind === "heart" ? (
          <div key={f.key} onAnimationEnd={done(f.key)} className="cp-float cp-heart" style={{ left: f.x, top: f.y }}>
            <svg width="22" height="20" viewBox="0 0 24 22" aria-hidden>
              <defs>
                {/* 一人一半：两种颜色直接渐变，中间会混成发脏的褐色 */}
                <linearGradient id={`cp-h${f.key}`} x1="0" y1="0" x2="1" y2="0">
                  <stop offset=".5" stopColor={f.c1} />
                  <stop offset=".5" stopColor={f.c2} />
                </linearGradient>
              </defs>
              <path d="M12 21s-8.5-5.3-10.6-10.2C-.1 7.2 2.2 2 6.9 2c2.3 0 4 1.3 5.1 3 1.1-1.7 2.8-3 5.1-3 4.7 0 7 5.2 5.5 8.8C20.5 15.7 12 21 12 21z"
                fill={`url(#cp-h${f.key})`} stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
            </svg>
          </div>
        ) : (
          <div key={f.key} onAnimationEnd={done(f.key)} className="cp-float cp-zzz" style={{ left: f.x, top: f.y }} aria-hidden>
            <span>z</span><span>z</span><span>Z</span>
          </div>
        )
      ))}
    </>
  );
}
